import styled from "@emotion/styled";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Container,
  Drawer,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Toolbar from "@mui/material/Toolbar";
import { getAnalytics, logEvent } from "firebase/analytics";
import { motion, useScroll, useTransform } from "framer-motion";
import React, { useEffect, useState } from "react";
import { Link, useLoaderData, useLocation } from "react-router-dom";
import { isProduction } from "../Utils/environment";
import { scrollToAnchor } from "../Utils/scroll";

const navbarButtons = [
  { title: "About", link: "/#/about" },
  { title: "Capabilities", link: "/#/capabilities" },
  { title: "Work", link: "/#/work" },
  { title: "Process", link: "/#/process" },
];

export interface Item {
  title: string;
  link: string;
}

interface DrawerNavButtonProps {
  item: Item;
  currentPath: string;
}

const DrawerNavButton: React.FC<DrawerNavButtonProps> = ({
  item,
  currentPath,
}) => {
  const theme = useTheme();
  return (
    <Button
      className={`navbar-button ${currentPath === item.link && "active"}`}
      component={Link}
      to={item.link}
      onClick={(e) => {
        e.preventDefault();
        scrollToAnchor(item);
      }}
      sx={{ display: "block", textAlign: "left", width: "100%" }}
    >
      <Typography
        variant={"textSMMedium"}
        color={theme.palette.primary.main}
        sx={{ padding: "8px 16px", textTransform: "none" }}
        fontWeight={400}
      >
        {item.title}
      </Typography>
    </Button>
  );
};

const Navbar: React.FC = () => {
  // const { innerHeight: height } = window;
  // const touchscreen = isTouchDevice();
  const { pathname } = useLocation();
  const { scrollY } = useScroll();
  const theme = useTheme();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const opacity = useTransform(
    scrollY,
    [isMobile ? 150 : 350, isMobile ? 151 : 351],
    [pathname === "/" ? 0 : 1, 1]
  );

  const isMdDown = useMediaQuery(theme.breakpoints.down("lg"));
  const isXsDown = useMediaQuery(theme.breakpoints.down("sm"));
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  const [navTheme, setNavTheme] = useState<string>("");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const pageRouteData = useLoaderData();
  const analytics = isProduction() ? getAnalytics() : null;
  // const navigate = useNavigate();

  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open);
  };

  useEffect(() => {
    if (pageRouteData != null && (pageRouteData as any).initialTheme != null) {
      setNavTheme((pageRouteData as any).initialTheme);
    }
  }, [pageRouteData]);

  const drawerContent = (
    <Box
      component="div"
      sx={{ width: 250, height: "100%", padding: 2 }}
      onClick={toggleDrawer(false)}
      bgcolor={"white"}
      role="presentation"
    >
      <Stack gap={2} direction="column" height="100%">
        <Box component="div">
          <DrawerNavButton
            item={{ title: "Home", link: "/#/hero-section" }}
            currentPath={pathname}
          />
          {navbarButtons.map((item, index) => (
            <DrawerNavButton key={index} item={item} currentPath={pathname} />
          ))}
        </Box>
        <Button
          component={Link}
          to="/#/contact"
          onClick={(e) => {
            e.preventDefault();
            scrollToAnchor({ title: "Contact", link: "/#/contact" });
          }}
          sx={{
            background: theme.palette.pink.main,
            padding: "0.5rem 1.5rem",
            marginRight: "1.5rem",
            marginLeft: "1.5rem",
          }}
        >
          <Typography
            color={theme.palette.common.white}
            variant="textSMMedium"
            textTransform="none"
          >
            Contact
          </Typography>
        </Button>

        <Stack direction="row" flex={1}></Stack>

        <Stack
          direction={{
            xs: "column-reverse",
            sm: "column-reverse",
            md: "row",
          }}
          alignItems="center"
          justifyContent="space-between"
          gap={{ xs: 2, sm: 2, md: 0 }}
        >
          <Typography variant="body2">
            Copyright, Pencil to Polish {new Date().getFullYear()}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );

  return (
    <Box
      component="div"
      sx={{
        overflow: "hidden",
        position: "fixed",
        width: "100%",
        flexGrow: 1,
        zIndex: 1000,
      }}
    >
      <Box
        component="div"
        sx={{
          clip: "rect(1px, 1px, 1px, 1px)",
          position: "absolute",
          whiteSpace: "nowrap",
          overflow: "hidden",
          height: 1,
          width: 1,
        }}
      >
        <Link
          to="#main-content"
          onClick={(e) => {
            e.preventDefault();
            scrollToAnchor({ title: "Skip links", link: "/#/main-content" });
          }}
          style={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.yellow.main,
            transform: "translateX(-50%)",
            textDecoration: "none",
            position: "absolute",
            padding: "8px 16px",
            zIndex: 2000,
            left: "50%",
          }}
        >
          Skip to main content
        </Link>
      </Box>

      <motion.div
        initial={{ opacity: 0, y: -80 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: pathname === "/" ? 0.6 : 0,
          delay: pathname === "/" ? 0.3 : 0,
        }}
      >
        <Container maxWidth="xl">
          <NavBackground
            style={{
              transition: "opacity 0.3s ease-in-out",
              backgroundColor: "#01374399",
              opacity,
            }}
          />

          <StyledAppBar
            color="transparent"
            position="static"
            elevation={0}
            theme={theme}
          >
            {isMdDown && (
              <Toolbar style={{ padding: 0 }}>
                <Box component="div" flexGrow={1}>
                  <Button
                    title="Pencil to Polsih"
                    component={Link}
                    to="/"
                    sx={{ height: 40, pl: 0 }}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToAnchor({
                        title: "Home",
                        link: "/#/hero-section",
                      });
                    }}
                  >
                    <NavCapabilities
                      variant="h3"
                      color={
                        navTheme === "dark"
                          ? theme.palette.common.white
                          : theme.palette.primary.main
                      }
                    >
                      <img
                        src={"/pencil_to_polish_logo.svg"}
                        style={{ marginTop: "1.25rem" }}
                        alt="Pencil to Polsih"
                        width="200"
                        loading="lazy"
                        decoding="async"
                      />
                    </NavCapabilities>
                  </Button>
                </Box>
                <Stack direction="row" gap={1}>
                  <Button
                    variant="text"
                    aria-label="menu"
                    sx={{
                      color: theme.palette.common.white,
                      minWidth: "auto",
                      border: "none",
                      mt: "-0.2rem",
                      pr: 0,
                    }}
                    onClick={toggleDrawer(true)}
                  >
                    <MenuIcon />
                  </Button>
                  <Drawer
                    anchor="right"
                    open={drawerOpen}
                    onClose={toggleDrawer(false)}
                  >
                    {drawerContent}
                  </Drawer>
                </Stack>
              </Toolbar>
            )}
            {isLgUp && (
              <Toolbar style={{ padding: 0 }}>
                <Box component="div" flexGrow={1}>
                  <motion.div
                    onClick={(e) => {
                      e.preventDefault();
                      window.scrollTo({
                        top: 0,
                        behavior: "smooth",
                      });

                      if (analytics) {
                        logEvent(analytics, "scroll_to_top_logo_click");
                      }
                    }}
                  >
                    <Button
                      title="Pencil to Polsih"
                      component={Link}
                      to="/"
                      sx={{ height: 40, pl: 0 }}
                    >
                      <NavCapabilities
                        variant="h3"
                        color={
                          navTheme === "dark"
                            ? theme.palette.common.white
                            : theme.palette.primary.main
                        }
                      >
                        <img
                          src={"/pencil_to_polish_logo.svg"}
                          style={{ marginTop: "1.25rem" }}
                          alt="Pencil to Polsih"
                          width="200"
                          loading="lazy"
                          decoding="async"
                        />
                      </NavCapabilities>
                    </Button>
                  </motion.div>
                </Box>

                <Stack gap={10} direction="row" alignItems="center">
                  <Box component="div" display={"flex"} gap={10}>
                    {navbarButtons.map((item, index) => (
                      <Button
                        key={index}
                        className={`navbar-button`}
                        component={Link}
                        to={item.link}
                        title={item.title}
                        onClick={(e) => {
                          e.preventDefault();
                          scrollToAnchor(item);
                        }}
                      >
                        <NavbarButtonText
                          className={`${pathname === item.link && "active"}`}
                          color={theme.palette.yellow.main}
                          variant={pathname === item.link ? "body1" : "body2"}
                        >
                          {item.title}
                        </NavbarButtonText>
                      </Button>
                    ))}
                  </Box>

                  <Button
                    component={Link}
                    to="/#/contact"
                    title="Contact"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToAnchor({ title: "Contact", link: "/#/contact" });
                    }}
                    sx={{
                      border: `1px solid ${theme.palette.yellow.main}`,
                      transition: "all 0.3s ease-in-out",
                      padding: "0.5rem 1.5rem",
                      borderRadius: "2px",

                      "&:hover": {
                        border: `1px solid ${theme.palette.yellow}`,
                        backgroundColor: theme.palette.yellow.main,
                        "& .MuiTypography-root": {
                          color: theme.palette.primary.main,
                        },
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      color={theme.palette.yellow.main}
                      textTransform="none"
                    >
                      Contact
                    </Typography>
                  </Button>
                </Stack>
              </Toolbar>
            )}
          </StyledAppBar>
        </Container>
      </motion.div>
    </Box>
  );
};

export default Navbar;

interface StyledAppBarProps {
  theme: any;
}

const StyledAppBar = styled(AppBar)<StyledAppBarProps>`
  .navbar-button {
    padding: 0.25rem 0.4rem;
    text-transform: none;
  }
`;

interface NavbarButtonTextProps {}

const NavbarButtonText = styled(Typography)<NavbarButtonTextProps>`
  white-space: nowrap;
`;

const NavBackground = styled(motion.div)`
  mask-image: linear-gradient(
    0deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 1) 7%,
    rgba(0, 0, 0, 1) 100%
  );
  backdrop-filter: blur(20px);
  position: fixed;
  height: 4.25rem;
  width: 100%;
  z-index: -1;
  left: 0;

  @media (max-width: 600px) {
    height: 3.4rem;
  }
`;

interface NavbarCapabilitiesProps {}

const NavCapabilities = styled(Typography)<NavbarCapabilitiesProps>`
  padding: 0rem 0.4rem 0 0;
  text-transform: uppercase;
  font-family: "Poppins";
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 64px;
  span {
    font-weight: 200;
  }

  // -webkit-text-fill-color: transparent;
  // -webkit-background-clip: text;
  // background-clip: text;
  white-space: nowrap;
`;
